// File Location: src/pages/dashboard/PolicyDashboard.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  FileText,
  User,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Menu,
  X,
  Heart,
  Building,
  FolderOpen,
  CreditCard
} from 'lucide-react';
// eslint-disable-next-line no-undef
/* global deriveStageForPolicy */
import ApplicationsList from '../policy/ApplicationsList';
import CounsellingFilter from '../../component/policy/CounsellingFilter';
import PolicyHistory from '../policy/PolicyHistory';
import PolicyProfile from '../policy/PolicyProfile';
import { getPolicyWriterStats } from '../../services/policyWriterService';
import { getAllApplicationsAdmin } from '../../services/doctorService';
import { useAuth } from '../../context/AuthContext';
import { SignOutIconButton } from '../../component/common/SignOutButton';
import samhealthLogo from '../../assets/samhealth.png';
import NotificationBell from '../../components/common/NotificationBell';
import RoleSwitcher from '../../components/common/RoleSwitcher';
import UniversalSearch from '../../components/common/UniversalSearch';
import PolicyENachHospitalView from '../policy/PolicyENachHospitalView';

// Move CounsellingApplicationsList outside to prevent re-creation
const CounsellingApplicationsList = React.memo(({
  counsellingApplications,
  onApplicationsLoad,
  onScheduleCounselling
}) => {
  const [counsellingLoading, setCounsellingLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState('all');

  const isKypAccepted = (application) => {
    const mv = application?.workflow?.medicalVerification || {};
    const ack = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
    return ack === 'accepted';
  };

  const isDispatchApproved = (application) => {
    const w = application?.workflow || {};
    const ds = w.dischargeSettlement || {};
    const dispatchObj = w.dispatch || w.dispatches || {};
    return (
      w?.dispatchClearance?.status === 'approved' ||
      dispatchObj?.status === 'completed' ||
      ds?.dispatchClearance?.status === 'approved' ||
      ds?.dispatchReview?.status === 'approved'
    );
  };

  const ApprovedDocsChip = ({ application }) => {
    const w = application?.workflow || {};
    const ds = w.dischargeSettlement || {};
    const checklist = ds?.scrutinyChecklist;
    if (Array.isArray(checklist) && checklist.length > 0) {
      const approved = checklist.filter(i => (i?.status || '').toLowerCase() === 'approved').length;
      const total = checklist.length;
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
          Approved Docs: {approved} / {total}
        </span>
      );
    }
    return null;
  };

  const handleFilterChange = useCallback((newFilters) => {
    // forward filters to parent if needed in future
  }, []);

  const handleApplicationsLoad = useCallback((applications) => {
    onApplicationsLoad(applications);
    setCounsellingLoading(false); // Set loading to false when applications are loaded
  }, [onApplicationsLoad]);

  const [bucketStats, setBucketStats] = useState({ total: 0, kypInitialPending: 0, dischargePending: 0, urgent: 0, emergency: 0 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Patient Counselling</h2>
        <p className="text-gray-600">Manage KYP Initial Counselling and Discharge Counselling</p>
      </div>

      {/* Buckets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 pt-4 pb-2">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'all', label: 'All', count: bucketStats.total },
              { key: 'kyp', label: 'KYP Initial', count: bucketStats.kypInitialPending },
              { key: 'discharge', label: 'Discharge', count: bucketStats.dischargePending },
              { key: 'emergency', label: 'Emergency', count: bucketStats.emergency },
              { key: 'urgent', label: 'Urgent', count: bucketStats.urgent },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveBucket(tab.key)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors flex items-center gap-2 ${activeBucket === tab.key
                  ? 'bg-teal-50 text-teal-700 border-teal-200'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span>{tab.label}</span>
                {typeof tab.count === 'number' ? (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">{tab.count}</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      </div>

      <CounsellingFilter
        onFilterChange={handleFilterChange}
        onApplicationsLoad={handleApplicationsLoad}
        quickBucket={activeBucket}
        onStatsChange={setBucketStats}
      />

      {/* Applications List */}
      {!counsellingLoading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {counsellingApplications.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {counsellingApplications.map((application, index) => (
                <div key={application.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {application.patient?.name || 'Patient Name'}
                        </h3>
                        <span className="text-sm text-gray-500">
                          ID: {application.applicationId}
                        </span>
                        {application.emergency === true && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Emergency
                          </span>
                        )}
                        {application.priority === 'urgent' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Urgent
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Hospital:</span>
                          <p className="font-medium">{application.hospitalName || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Insurance:</span>
                          <p className="font-medium">{application.policy?.insuranceCompany || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Coverage:</span>
                          <p className="font-medium">₹{application.policy?.coverageAmount?.toLocaleString('en-IN') || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Submitted:</span>
                          <p className="font-medium">{application.submittedAt?.toDate()?.toLocaleDateString() || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Counselling Status */}
                      <div className="mt-4 flex items-center space-x-4">
                        {application.counsellingNeeded?.kypInitial && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                            KYP Initial Counselling Pending
                          </span>
                        )}
                        {application.counsellingNeeded?.discharge && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            Discharge Counselling Pending
                          </span>
                        )}
                    {isKypAccepted(application) && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        KYP Accepted
                      </span>
                    )}
                    {isDispatchApproved(application) && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Dispatch Approved
                      </span>
                    )}
                    <ApprovedDocsChip application={application} />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onScheduleCounselling(application)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Schedule Counselling
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Counselling Required</h3>
              <p className="text-gray-600">All applications are up to date with counselling requirements.</p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
});

const PolicyDashboard = () => {
  const { userData: user } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [counsellingApplications, setCounsellingApplications] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    pendingReview: 0,
    reviewedToday: 0,
    totalReviewed: 0,
    averageReviewTime: 0,
    emergencyPending: 0,
    approvalRate: 0,
    approvedCount: 0,
    rejectedCount: 0,
    pendingInfoCount: 0,
    totalCoverageReviewed: 0,
    totalClaimAmount: 0,
    averageClaimRatio: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // All Applications (policy team) state
  const [allApplications, setAllApplications] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allSearch, setAllSearch] = useState("");
  const [allPage, setAllPage] = useState(1);
  const [allTotalPages, setAllTotalPages] = useState(1);
  const [allPolicyStage, setAllPolicyStage] = useState('all');
  const [allPolicyStageCounts, setAllPolicyStageCounts] = useState({});
  const [allClientBucket, setAllClientBucket] = useState('none'); // none | query_reply_received | kyc_approved | fal_accepted
  const [allAdditionalCounts, setAllAdditionalCounts] = useState({});

  // Challenged (KYP reassigned to Policy) state must be declared before use in sidebarItems
  const [challengedApps, setChallengedApps] = useState([]);
  const [challengedLoading, setChallengedLoading] = useState(false);

  // Stable callback functions to prevent re-renders
  const handleCounsellingApplicationsLoad = useCallback((applications) => {
    setCounsellingApplications(applications);
  }, []);

  const handleScheduleCounselling = useCallback((application) => {
    setSelectedApplication(application);
    setActiveSection('applications');
  }, []);

  // Reset selected application when leaving applications section
  const prevActiveSection = useRef(activeSection);
  useEffect(() => {
    if (prevActiveSection.current === 'applications' && activeSection !== 'applications') {
      setSelectedApplication(null);
    }
    prevActiveSection.current = activeSection;
  }, [activeSection]);

  const sidebarItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      count: null
    },
    {
      id: 'applications',
      label: 'Pending Applications',
      icon: FileText,
      count: dashboardStats.pendingReview
    },
    {
      id: 'all-applications',
      label: 'All Applications',
      icon: BarChart3,
      count: null
    },
    {
      id: 'challenged',
      label: 'Challenged (KYP)',
      icon: AlertTriangle,
      count: challengedApps?.length || 0
    },
    {
      id: 'counselling',
      label: 'Counselling',
      icon: Heart,
      count: counsellingApplications?.length || dashboardStats.pendingCounselling || 0
    },

    {
      id: 'enach',
      label: 'e-NACH',
      icon: CreditCard,
      count: null
    },

    {
      id: 'history',
      label: 'Policy History',
      icon: Shield,
      count: null
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: FolderOpen,
      count: null
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      count: null
    }
  ];

  const loadDashboardData = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const result = await getPolicyWriterStats(user.uid);
      if (result.success) {
        setDashboardStats(result.stats);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (user?.uid) {
      loadDashboardData();
    }
  }, [user, loadDashboardData]);



  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };
  const deriveStageForPolicy = (application) => {
    const w = application?.workflow || {};
    const mv = w.medicalVerification || {};
    const pv = w.policyVerification || {};
    const ds = w.dischargeSettlement || {};

    // 1) Query: disputes or policy clarification pending
    if (
      application?.status === 'dispute_pending' ||
      application?.status === 'pending_policy_clarification' ||
      pv?.status === 'pending_info'
    ) {
      return 'query';
    }

    // 2) KYP challenged by hospital
    const kypAckStatus = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
    if (kypAckStatus === 'challenged') {
      return 'kyp_challenge';
    }

    // 3) Discharge counselling pending (post-approval / discharge started) and not completed
    const dc = pv?.dischargeCounselling;
    const dischargeCounsellingPending = (!dc || ['pending', 'scheduled', 'in-progress'].includes(dc?.status)) && (
      application?.dischargeInitiated || !!ds?.status || pv?.status === 'approved' || application?.status === 'approved' || application?.status === 'policy_approved'
    );
    if (dischargeCounsellingPending) {
      return 'discharge_counselling_pending';
    }

    // 4) KYP initial counselling pending (pre-discharge) and not completed
    const kic = pv?.kypInitialCounselling;
    const kypCounsellingPending = (!kic || ['pending', 'scheduled', 'in-progress'].includes(kic?.status)) && (
      pv?.status === 'approved' || application?.status === 'policy_approved' || application?.status === 'policy_review_pending' || application?.status === 'medical_approved'
    );
    if (kypCounsellingPending) {
      return 'kyp_counselling_pending';
    }

    // 5) New (default)
    return 'new';
  };
  // Finance-parity bucket helpers for Policy
  const policyBucketKeysForApp = React.useCallback((a) => {
    const keys = [];
    const w = a?.workflow || {};
    const fin = w.financialVerification || {};
    const ds = w.dischargeSettlement || {};
    const enach = w.enach || w.eNach || {};
    const dispatch = w.dispatch || w.dispatches || {};
    const pod = w.proofOfDispatch || w.pod || {};
    const disb = w.disbursal || {};
    const mv = w.medicalVerification || {};

    // 1. Query raised by Finance - check multiple conditions
    const depRaised = Array.isArray(fin.dependencies) && fin.dependencies.some(d => (d?.raisedBy?.department || d?.department) === 'financial' && (d.status === 'raised' || d.status === 'pending'));
    const queryRaisedFinance = (
      a?.status === 'pending_financial_clarification' ||
      a?.status === 'dispute_pending' ||
      fin?.status === 'pending_info' ||
      fin?.status === 'under_review' ||
      depRaised
    );
    if (queryRaisedFinance) keys.push('query_raised_finance');

    // 2. Query reply received for Finance
    const depReplied = Array.isArray(fin.dependencies) && fin.dependencies.some(d => (d?.raisedBy?.department || d?.department) === 'financial' && (d.status === 'reply_received' || d.status === 'addressed'));
    if (a?.status === 'under_review' && depReplied) keys.push('query_reply_finance');

    // 3. Finance approved/reject - check multiple status fields
    const financeApproved = (
      (fin?.status || '').toLowerCase() === 'approved' ||
      a?.status === 'financial_approved' ||
      fin?.assessment?.status === 'approved' ||
      fin?.verifiedBy !== null ||
      fin?.verifiedAt !== null
    );
    if (financeApproved) keys.push('financial_approved');

    const financeRejected = (
      (fin?.status || '').toLowerCase() === 'rejected' ||
      a?.status === 'financial_rejected' ||
      fin?.assessment?.status === 'rejected' ||
      a?.status === 'rejected'
    );
    if (financeRejected) keys.push('financial_reject');

    // 4. FAL generated/accepted - check multiple fields
    const falGenerated = (
      !!ds?.finalApprovalLetterUrl ||
      !!ds?.finalApprovalLetterId ||
      ds?.status === 'computed' ||
      ds?.status === 'generated'
    );
    if (falGenerated) keys.push('fal_generated');

    const falAccepted = (
      ds?.hospitalAcknowledgement?.status === 'accepted' ||
      ds?.status === 'accepted' ||
      (Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'accepted'))
    );
    if (falAccepted) keys.push('fal_accepted');

    // 5. eNach - check multiple status fields
    if (enach?.status === 'pending' || enach?.status === 'initiated') keys.push('enach_pending');
    if (enach?.status === 'completed' || enach?.hospitalCompletedAt) keys.push('enach_completed');

    // 6. Scrutiny - check dispatch and scrutiny status
    const scrutinyPending = (
      dispatch?.status === 'pending' ||
      ds?.scrutinySubmission?.status === 'submitted' ||
      ds?.scrutinyChecklist?.some(item => item.status === 'pending') ||
      ds?.dispatchReview?.status === 'requested'
    );
    if (scrutinyPending) keys.push('scrutiny_pending');

    const scrutinyCompleted = (
      dispatch?.status === 'completed' ||
      ds?.dispatchClearance?.status === 'approved' ||
      ds?.scrutinyReview?.status === 'checker_confirmed'
    );
    if (scrutinyCompleted) keys.push('scrutiny_completed');

    // 7. POD (Proof of Dispatch) - check multiple fields
    if (pod?.status === 'pending' || pod?.status === 'initiated') keys.push('pod_pending');
    if (pod?.status === 'completed' || pod?.acknowledgedAt) keys.push('pod_completed');

    // 8. Disbursement - check disbursal status
    if (disb?.status === 'pending' || disb?.status === 'initiated') keys.push('disbursed_pending');
    if (disb?.status === 'completed' || disb?.completedAt) keys.push('disbursed');

    // 9. New applications - fallback for early stage
    const isNew = (
      !fin?.status && 
      !ds?.finalApprovalLetterUrl && 
      !enach?.status && 
      !dispatch?.status && 
      !pod?.status && 
      !disb?.status &&
      !mv?.kypGenerated &&
      a?.status !== 'rejected'
    );
    if (isNew) keys.push('new');

    return keys;
  }, []);

  const policyPrimaryBucketForApp = React.useCallback((a) => {
    const keys = policyBucketKeysForApp(a);
    const priority = ['disbursed', 'disbursed_pending', 'pod_completed', 'pod_pending', 'scrutiny_completed', 'scrutiny_pending', 'enach_completed', 'enach_pending', 'fal_accepted', 'fal_generated', 'financial_approved', 'financial_reject', 'query_reply_finance', 'query_raised_finance', 'new'];
    for (const p of priority) if (keys.includes(p)) return p;
    return 'new';
  }, [policyBucketKeysForApp]);

  const buildPolicyFinanceCounts = React.useCallback((apps) => {
    const counts = { all: 0, new: 0, query_raised_finance: 0, query_reply_finance: 0, financial_approved: 0, financial_reject: 0, fal_generated: 0, fal_accepted: 0, enach_pending: 0, enach_completed: 0, scrutiny_pending: 0, scrutiny_completed: 0, pod_pending: 0, pod_completed: 0, disbursed_pending: 0, disbursed: 0 };
    apps.forEach((a) => {
      counts.all += 1;
      // Count all buckets the application belongs to, not just the primary one
      const keys = policyBucketKeysForApp(a);
      keys.forEach(key => {
        counts[key] = (counts[key] || 0) + 1;
      });
      // If no keys were added, default to 'new'
      if (keys.length === 0) {
        counts.new += 1;
      }
    });
    return counts;
  }, [policyBucketKeysForApp]);

  const matchesPolicyBucket = React.useCallback((a, key) => {
    if (!key || key === 'all') return true;
    const keys = new Set(policyBucketKeysForApp(a));
    return keys.has(key);
  }, [policyBucketKeysForApp]);

  const [allApplicationsRaw, setAllApplicationsRaw] = useState([]);

  // Load All Applications for policy team
  const loadAllApplications = useCallback(async () => {
    setAllLoading(true);
    try {
      const filters = { searchTerm: allSearch, _fetchAll: true };
      const result = await getAllApplicationsAdmin(
        filters,
        allPage,
        10
      );
      if (result.success) {
        const raw = result.applications || [];
        const counts = buildPolicyFinanceCounts(raw);
        const filtered = raw.filter((a) => matchesPolicyBucket(a, allPolicyStage));
        setAllApplicationsRaw(raw);
        setAllApplications(filtered);
        setAllTotalPages(1);
        setAllPolicyStageCounts(counts);
        setAllAdditionalCounts({});
      } else {
        setAllApplications([]);
        setAllTotalPages(1);
        setAllPolicyStageCounts({});
        setAllAdditionalCounts({});
      }
    } catch (err) {
      console.error('Error loading all applications (policy):', err);
      setAllApplications([]);
      setAllTotalPages(1);
      setAllPolicyStageCounts({});
      setAllAdditionalCounts({});
    } finally {
      setAllLoading(false);
    }
  }, [allSearch, allPage, allPolicyStage]);
  const loadChallenged = useCallback(async () => {
    setChallengedLoading(true);
    try {
      const result = await getAllApplicationsAdmin(
        { kypChallengedTo: 'policy' },
        1,
        20
      );
      setChallengedApps(result.success ? (result.applications || []) : []);
    } catch (e) {
      console.error('Error loading challenged (policy):', e);
      setChallengedApps([]);
    } finally {
      setChallengedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'challenged') {
      loadChallenged();
    }
  }, [activeSection, loadChallenged]);

  // Load and default filter for e-NACH section
  useEffect(() => {
    if (activeSection === 'enach') {
      // ensure data is loaded and default to pending view
      setAllPolicyStage('enach_pending');
      loadAllApplications();
    }
  }, [activeSection, loadAllApplications]);

  const renderChallenged = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Challenged KYP</h1>
          <p className="text-gray-600">Applications reassigned by doctors for policy verification</p>
        </div>
        <button onClick={loadChallenged} className="btn-outline"><RefreshCw className="h-4 w-4 mr-2" />Refresh</button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {challengedLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-2"></div>
            <span className="text-gray-600">Loading challenged cases...</span>
          </div>
        ) : challengedApps.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No challenged cases</h3>
            <p className="text-gray-600">Nothing to review right now.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {challengedApps.map((application) => (
              <div key={application.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  e.stopPropagation();
                  const url = `${window.location.origin}/policy/details/${application.applicationId}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                  return;
                }
                navigate(`/policy/details/${application.applicationId}`);
              }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">{application.patient?.name || 'Patient'}</div>
                    <div className="text-xs text-gray-500">{application.applicationId}</div>
                    {application.workflow?.medicalVerification?.kypReassignment?.comment && (
                      <div className="mt-1 text-xs text-gray-600">Note: {application.workflow.medicalVerification.kypReassignment.comment}</div>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-700">
                    <div className="flex items-center justify-end gap-2">
                      <Building className="h-3 w-3 text-gray-400" /> {application.hospitalName || '—'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Reassigned on {application.workflow?.medicalVerification?.kypReassignment?.at ? new Date(application.workflow.medicalVerification.kypReassignment.at.seconds ? application.workflow.medicalVerification.kypReassignment.at.seconds * 1000 : application.workflow.medicalVerification.kypReassignment.at).toLocaleString() : '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Trigger load when entering the section or when search/page changes
  useEffect(() => {
    if (activeSection === 'all-applications') {
      loadAllApplications();
    }
  }, [activeSection, loadAllApplications]);

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Policy Writer Dashboard</h1>
          <p className="text-gray-600">Review and verify insurance policies for medical claims</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 shadow-soft"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Pending Reviews */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Reviews</p>
              <p className="text-2xl font-bold text-orange-600">{dashboardStats.pendingReview}</p>
              {dashboardStats.emergencyPending > 0 && (
                <p className="text-xs text-red-600 mt-1">
                  {dashboardStats.emergencyPending} Emergency cases
                </p>
              )}
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Today's Reviews */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Reviewed Today</p>
              <p className="text-2xl font-bold text-blue-600">{dashboardStats.reviewedToday}</p>
              <p className="text-xs text-gray-500 mt-1">
                Avg: {dashboardStats.averageReviewTime} min/case
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Approval Rate */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approval Rate</p>
              <p className="text-2xl font-bold text-green-600">{dashboardStats.approvalRate}%</p>
              <p className="text-xs text-gray-500 mt-1">
                {dashboardStats.approvedCount} approved / {dashboardStats.totalReviewed} total
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Coverage Reviewed */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Coverage Reviewed</p>
              <p className="text-2xl font-bold text-purple-600">
                ₹{(dashboardStats.totalCoverageReviewed / 100000).toFixed(1)}L
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Claim ratio: {dashboardStats.averageClaimRatio}%
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Review Status Breakdown */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Status Breakdown</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Approved</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-900">{dashboardStats.approvedCount}</span>
                <span className="text-xs text-gray-500 ml-1">
                  ({dashboardStats.totalReviewed > 0 ? Math.round((dashboardStats.approvedCount / dashboardStats.totalReviewed) * 100) : 0}%)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Rejected</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-900">{dashboardStats.rejectedCount}</span>
                <span className="text-xs text-gray-500 ml-1">
                  ({dashboardStats.totalReviewed > 0 ? Math.round((dashboardStats.rejectedCount / dashboardStats.totalReviewed) * 100) : 0}%)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Pending Info</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-gray-900">{dashboardStats.pendingInfoCount}</span>
                <span className="text-xs text-gray-500 ml-1">
                  ({dashboardStats.totalReviewed > 0 ? Math.round((dashboardStats.pendingInfoCount / dashboardStats.totalReviewed) * 100) : 0}%)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button
              onClick={() => setActiveSection('applications')}
              className="w-full flex items-center justify-between p-3 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span className="text-sm font-medium">Review Applications</span>
              </div>
              <div className="flex items-center space-x-1">
                {dashboardStats.pendingReview > 0 && (
                  <span className="bg-teal-200 text-teal-800 text-xs px-2 py-1 rounded-full">
                    {dashboardStats.pendingReview}
                  </span>
                )}
                <ArrowRight className="h-4 w-4" />
              </div>
            </button>

            <button
              onClick={() => setActiveSection('history')}
              className="w-full flex items-center justify-between p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Shield className="h-4 w-4" />
                <span className="text-sm font-medium">View History</span>
              </div>
              <ArrowRight className="h-4 w-4" />
            </button>

            {dashboardStats.emergencyPending > 0 && (
              <button
                onClick={() => setActiveSection('applications')}
                className="w-full flex items-center justify-between p-3 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">Emergency Cases</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="bg-red-200 text-red-800 text-xs px-2 py-1 rounded-full">
                    {dashboardStats.emergencyPending}
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600">Efficiency Score</span>
                <span className="text-sm font-medium text-gray-900">
                  {dashboardStats.averageReviewTime > 0 ? Math.max(10, 100 - dashboardStats.averageReviewTime) : 0}/100
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-teal-600 h-2 rounded-full"
                  style={{
                    width: `${dashboardStats.averageReviewTime > 0 ? Math.max(10, 100 - dashboardStats.averageReviewTime) : 0}%`
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600">Quality Score</span>
                <span className="text-sm font-medium text-gray-900">{dashboardStats.approvalRate}/100</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${dashboardStats.approvalRate}%` }}
                ></div>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Reviews</span>
                <span className="text-lg font-bold text-gray-900">{dashboardStats.totalReviewed}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Priority Applications</h3>
          <button
            onClick={() => setActiveSection('applications')}
            className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center space-x-1"
          >
            <span>View All</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {dashboardStats.pendingReview > 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {dashboardStats.pendingReview} Applications Waiting
            </h4>
            <p className="text-gray-600 mb-4">
              Review pending insurance policies to help patients get faster approvals
            </p>
            <button
              onClick={() => setActiveSection('applications')}
              className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 mx-auto shadow-soft"
            >
              <FileText className="h-4 w-4" />
              <span>Start Reviewing</span>
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h4>
            <p className="text-gray-600">No pending policy reviews at the moment</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderAllApplications = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">All Applications</h1>
          <p className="text-gray-600">View every application across the system</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="text"
            placeholder="Search by ID, patient, diagnosis, hospital, doctor"
            value={allSearch}
            onChange={(e) => {
              setAllPage(1);
              setAllSearch(e.target.value);
            }}
            className="input-field w-72"
          />
          <button onClick={() => { setAllSearch(''); setAllPage(1); }} className="btn-outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>
      </div>

      {/* All Applications - Finance-parity buckets (ordered) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'new', label: 'New applications' },
              { key: 'query_raised_finance', label: 'Query raised' },
              { key: 'query_reply_finance', label: 'Query reply received' },
              { key: 'financial_approved', label: 'Finance approved' },
              { key: 'financial_reject', label: 'Finance reject' },
              { key: 'fal_generated', label: 'FAL generated' },
              { key: 'fal_accepted', label: 'FAL accepted' },
              { key: 'enach_pending', label: 'E-NACH pending' },
              { key: 'enach_completed', label: 'E-NACH completed' },
              { key: 'scrutiny_pending', label: 'Scrutiny pending' },
              { key: 'scrutiny_completed', label: 'Scrutiny completed' },
              { key: 'pod_pending', label: 'POD pending' },
              { key: 'pod_completed', label: 'POD completed' },
              { key: 'disbursed_pending', label: 'Disbursed pending' },
              { key: 'disbursed', label: 'Disbursed' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  setAllPage(1);
                  setAllPolicyStage(tab.key);
                  setAllClientBucket('none');
                  setAllApplications(allApplicationsRaw.filter((a) => matchesPolicyBucket(a, tab.key)));
                  setAllLoading(true);
                }}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${allPolicyStage === tab.key
                    ? 'bg-teal-50 text-teal-700 border-teal-200'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <span>{tab.label}</span>
                {typeof allPolicyStageCounts[tab.key] === 'number' ? (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">{allPolicyStageCounts[tab.key]}</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {allLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-2"></div>
            <span className="text-gray-600">Loading applications...</span>
          </div>
        ) : allApplications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Found</h3>
            <p className="text-gray-600">Try adjusting your search.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medical</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital & Doctor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lifecycle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allApplications
                    .filter((application) => {
                      if (allClientBucket === 'none') return true;
                      const w = application?.workflow || {};
                      const mv = w.medicalVerification || {};
                      const pv = w.policyVerification || {};
                      const ds = w.dischargeSettlement || {};
                      const dispute = w.dispute || w.queries || {};
                      switch (allClientBucket) {
                        case 'query_reply_received':
                          return (
                            pv?.queryReplyReceived === true ||
                            dispute?.lastReplyFrom === 'hospital' ||
                            dispute?.lastReplyAt ||
                            application?.status === 'dispute_pending'
                          );
                        case 'kyc_approved':
                          return (mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus) === 'accepted';
                        case 'fal_accepted': {
                          const sanctions = w?.sanctions;
                          const falAck = ds?.hospitalAcknowledgement?.status;
                          const allFalAccepted = Array.isArray(sanctions) && sanctions.length > 0 && sanctions.every(s => s?.hospitalAcknowledgement?.status === 'accepted');
                          return falAck === 'accepted' || allFalAccepted;
                        }
                        default:
                          return true;
                      }
                    })
                    .map((application) => (
                      <tr
                        key={application.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            e.preventDefault();
                            e.stopPropagation();
                            const url = `${window.location.origin}/policy/details/${application.applicationId}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          navigate(`/policy/details/${application.applicationId}`);
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{application.applicationId}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-medium">{application.patient?.name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{application.patient?.age ? `${application.patient.age}Y` : ''}{application.patient?.gender ? `, ${application.patient.gender}` : ''}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{application.medical?.diagnosis || '—'}</div>
                          <div className="text-xs text-gray-500">{application.medical?.treatmentType ? `${application.medical.treatmentType?.charAt(0).toUpperCase()}${application.medical.treatmentType?.slice(1)}` : ''}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Building className="h-3 w-3 mr-1 text-gray-400" />
                            {application.hospitalName || '—'}
                          </div>
                          <div className="text-xs text-gray-500">{application.medical?.doctor?.name ? `Dr. ${application.medical.doctor.name}` : ''}</div>
                        </td>
                        {/* Lifecycle Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {renderLifecycleChips(application)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {application.submittedAt?.toDate ? application.submittedAt.toDate().toLocaleDateString() : (application.submittedAt ? new Date(application.submittedAt).toLocaleDateString() : '—')}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {allTotalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">Page {allPage} of {allTotalPages}</div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setAllPage((p) => Math.max(p - 1, 1))}
                      disabled={allPage === 1}
                      className="btn-outline text-sm"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setAllPage((p) => Math.min(p + 1, allTotalPages))}
                      disabled={allPage === allTotalPages}
                      className="btn-outline text-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Removed previous inline e-NACH table in favor of shared ENachProcessList
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'applications':
        return (
          <ApplicationsList
            initialSelectedApplication={selectedApplication}
            initialActiveTab={selectedApplication ? 'consultations' : 'details'}
            onApplicationSelect={setSelectedApplication}
          />
        );
      case 'all-applications':
        return renderAllApplications();
      case 'challenged':
        return renderChallenged();
      case 'counselling':
        return (
          <CounsellingApplicationsList
            counsellingApplications={counsellingApplications}
            onApplicationsLoad={handleCounsellingApplicationsLoad}
            onScheduleCounselling={handleScheduleCounselling}
          />
        );
      case 'enach':
        return <PolicyENachHospitalView />;
      case 'history':
        return <PolicyHistory />;
      case 'profile':
        return <PolicyProfile />;
      default:
        return renderDashboard();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center px-6 py-4">
          <div className="flex items-center space-x-4 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-600 hover:text-gray-900 lg:hidden"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <img src={samhealthLogo} alt="SamHealth" className="h-8 w-auto object-contain" />
                <p className="text-sm text-teal-600">Policy Writer Portal</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-1 justify-end">
            {/* Universal Search Bar */}
            <div className="w-64 hidden md:block">
              <UniversalSearch userRole="policy" />
            </div>

            <div className="flex items-center space-x-4 flex-shrink-0">
            <NotificationBell />
            <div className="text-right">
              <p className="text-sm font-medium text-gray-800">
                {user?.profile?.name || user?.email?.split('@')[0] || 'Policy Writer'}
              </p>
              <p className="text-xs text-gray-600">
                {user?.profile?.designation || 'Policy Verification'}
              </p>
            </div>
            <RoleSwitcher buttonClassName="border-gray-300 hover:border-teal-400 hover:text-teal-700" />
            {/* Removed decorative header icon */}
            <SignOutIconButton
              size="sm"
              showConfirmModal={true}
              confirmMode="modal"
              className="hover:bg-red-100"
            />
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"
            } fixed lg:sticky lg:translate-x-0 z-30 w-64 top-16 lg:top-16 h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] bg-white shadow-lg border-r border-gray-200 transition-transform duration-300 ease-in-out overflow-y-auto`}
        >
          <nav className="mt-8">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.id === 'documents') {
                      navigate('/documents');
                    } else {
                      setActiveSection(item.id);
                    }
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-6 py-3 text-left transition-colors duration-200 ${isActive
                      ? "bg-teal-50 text-teal-600 border-r-2 border-teal-500"
                      : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                    }`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" />
                    {item.label}
                  </div>
                  {item.count !== null && item.count > 0 && (
                    <span className="bg-teal-100 text-teal-800 text-xs font-medium px-2 py-1 rounded-full">
                      {item.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:ml-0 ml-64">{renderContent()}</main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default PolicyDashboard;

// Reusable lifecycle chips renderer for the dedicated Status column
function renderLifecycleChips(application) {
  const w = application?.workflow || {};
  const mv = w.medicalVerification || {};
  const mr = w.medicalReview || {};
  const ds = w.dischargeSettlement || {};
  const disb = w.disbursal || {};
  const dispatch = w.dispatch || w.dispatches || {};

  const chips = [];

  const isInProgress = mr?.status === 'in_progress' || application?.status === 'under_medical_review' || application?.status === 'submitted' || application?.status === 'medical_review_pending';
  if (isInProgress) chips.push({ label: 'Processing', cls: 'bg-blue-100 text-blue-800 border-blue-200' });

  if (mv?.kypGenerated) chips.push({ label: 'KYP Generated', cls: 'bg-green-100 text-green-800 border-green-200' });

  const kypAck = mv?.kypHospitalAcknowledgement?.status;
  if (kypAck === 'accepted') chips.push({ label: 'KYP Accepted', cls: 'bg-green-100 text-green-800 border-green-200' });
  else if (kypAck === 'challenged') chips.push({ label: 'KYP Challenged', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
  else if (kypAck === 'rejected') chips.push({ label: 'KYP Rejected', cls: 'bg-red-100 text-red-800 border-red-200' });

  const dischargeStarted = application?.dischargeInitiated || ds?.status;
  if (dischargeStarted) chips.push({ label: 'Discharge Started', cls: 'bg-purple-100 text-purple-800 border-purple-200' });

  if (ds?.finalApprovalLetterUrl) chips.push({ label: 'FAL Generated', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' });

  const falAck = ds?.hospitalAcknowledgement?.status;
  const sanctions = w?.sanctions;
  const anyFalChallenged = Array.isArray(sanctions) && sanctions.some(s => s?.hospitalAcknowledgement?.status === 'challenged');
  const anyFalRejected = Array.isArray(sanctions) && sanctions.some(s => s?.hospitalAcknowledgement?.status === 'rejected');
  const allFalAccepted = Array.isArray(sanctions) && sanctions.length > 0 && sanctions.every(s => s?.hospitalAcknowledgement?.status === 'accepted');
  if (falAck === 'accepted' || allFalAccepted) chips.push({ label: 'FAL Accepted', cls: 'bg-green-100 text-green-800 border-green-200' });
  else if (falAck === 'challenged' || anyFalChallenged) chips.push({ label: 'FAL Challenged', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
  else if (falAck === 'rejected' || anyFalRejected) chips.push({ label: 'FAL Rejected', cls: 'bg-red-100 text-red-800 border-red-200' });

  if (dispatch?.status) chips.push({ label: `Dispatch: ${String(dispatch.status).replace(/_/g, ' ')}`, cls: 'bg-sky-100 text-sky-800 border-sky-200' });
  else if (ds?.finalApprovalLetterUrl && !disb?.status) chips.push({ label: 'Dispatch Pending', cls: 'bg-sky-50 text-sky-700 border-sky-200' });

  if (disb?.status === 'disbursed') chips.push({ label: 'Disbursed', cls: 'bg-teal-100 text-teal-800 border-teal-200' });
  else if (disb?.status) chips.push({ label: `Disbursal: ${String(disb.status).replace(/_/g, ' ')}`, cls: 'bg-teal-50 text-teal-800 border-teal-200' });

  if (chips.length === 0) chips.push({ label: 'Processing', cls: 'bg-blue-50 text-blue-700 border-blue-200' });

  return (
    <div className="flex flex-wrap items-center gap-1">
      {chips.map((c, idx) => (
        <span key={idx} className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${c.cls}`}>
          {c.label}
        </span>
      ))}
    </div>
  );
}